import { useState } from "react";
import { Mail, Phone, Loader2, Check, UserRound, Wrench, Headphones } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/Motion";
import { Field } from "@/components/site/QuoteModal";
import { BookCall } from "@/components/site/BookCall";
import { submitLead, formatApiError, EMAIL, PHONE, PHONE_HREF } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", business: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitLead({ ...form, source: "contact" });
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" lines={["Let's talk about", "your business."]} sub="Send us a note and your dedicated account manager will get back to you. No pressure, no obligation." />
      <section id="contact-form" className="bg-white px-4 py-24 sm:px-6 lg:px-12">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-12">
          <Reveal className="space-y-5 lg:col-span-5">
            <Eyebrow>Reach us directly</Eyebrow>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="group flex items-center gap-4 rounded-3xl bg-[#0B2A6F] p-6 text-white transition-transform duration-300 hover:-translate-y-1">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-lime-400 text-[#050B1F]"><Mail className="h-5 w-5" /></span>
              <span className="min-w-0"><span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-lime-300">Email</span><span className="block break-all font-display text-lg font-bold sm:text-xl">{EMAIL}</span></span>
            </a>
            <a href={PHONE_HREF} data-testid="contact-phone-link" className="group flex items-center gap-4 rounded-3xl bg-lime-400 p-6 text-[#050B1F] transition-transform duration-300 hover:-translate-y-1">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#050B1F] text-lime-300"><Phone className="h-5 w-5" /></span>
              <span><span className="block font-mono text-[11px] uppercase tracking-[0.2em]">Call 866-Zerofees</span><span className="block font-display text-xl font-bold">{PHONE}</span></span>
            </a>
            {[[UserRound, "Dedicated account manager", "One point of contact for your account."], [Wrench, "Tech support", "Help with terminals, POS and connections."], [Headphones, "Customer service", "Questions about your account, answered."]].map(([I, t, d]) => (
              <div key={t} className="flex items-start gap-4 rounded-3xl border border-emerald-900/10 p-6">
                <I className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
                <div><p className="font-display text-lg font-bold">{t}</p><p className="text-sm text-emerald-950/75">{d}</p></div>
              </div>
            ))}
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="rounded-[2rem] border border-emerald-900/10 bg-[#EAF3FF] p-6 sm:p-10">
              {done ? (
                <div className="py-16 text-center" data-testid="contact-success">
                  <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-lime-400"><Check className="h-8 w-8" strokeWidth={3} /></div>
                  <p className="mt-6 font-display text-3xl font-extrabold">Message sent.</p>
                  <p className="mt-2 text-emerald-950/75">We'll be in touch shortly.</p>
                  <button onClick={() => setDone(false)} data-testid="contact-send-another-btn" className="mt-8 text-sm font-semibold text-emerald-700 underline underline-offset-4">Send another message</button>
                </div>
              ) : (
                <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="contact-form">
                  <Field label="Name" testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                  <Field label="Business" testid="contact-business-input" value={form.business} onChange={set("business")} placeholder="Main Street Cafe" />
                  <Field label="Email" testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@business.com" />
                  <Field label="Phone" testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
                  <label className="block sm:col-span-2">
                    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-800">How can we help?</span>
                    <textarea data-testid="contact-message-input" rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your business and what you're looking for."
                      className="w-full rounded-xl border border-emerald-900/15 bg-white px-4 py-3 text-[15px] outline-none placeholder:text-emerald-900/40 focus:border-emerald-600 focus:ring-4 focus:ring-lime-300/40" />
                  </label>
                  <button disabled={busy} data-testid="contact-submit-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0B2A6F] px-8 py-4 text-sm font-bold text-white transition-[background-color,transform] hover:bg-[#050B1F] active:scale-95 disabled:opacity-60 sm:col-span-2 sm:w-fit">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send message
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
      <BookCall />
    </div>
  );
}
